import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { contestsApi } from '../api/performance'
import { type ContestWithScope, type ContestDetailWithScope, listContestsSorted, deleteContestGuarded } from '../api/contests'
import type { ConfigurationUsageRow } from '../api/pipelines'
import { UsageConfirmDialog } from '../components/UsageConfirmDialog'
import { SortKeyTh, SortResetLink } from '../components/SortKeyTh'
import { api } from '../api/client'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'
import {
  FormSection, Page, PageHeader, Callout, DataTable, DataTableEmptyRow, Pagination, LoadingState, StatusPill, Dialog,
  Button, IconButton, TextField, Select, Icon, useSnackbar, type PillTone, useConfirm, FilterBar,
} from '../components/ui'

const CONTEST_TYPE_LABELS: Record<string, string> = {
  revenue: 'Fatturato', volume: 'Volume contratti', product: 'Prodotto specifico',
}

/**
 * TASK-0183 (REQ-021-002) — the applied vocabulary for `Contest.Scope`, character-for-character
 * identical to `ContestScopes` (`Domain/Contest.cs`): same 6 values, same order, same exact Italian
 * text. Kept as a plain array (not a labels map like `CONTEST_TYPE_LABELS`) because the backend
 * vocabulary already IS the display text — there is no separate wire-value/label pair the way
 * `contestType`'s `revenue|volume|product` codes need `CONTEST_TYPE_LABELS` to translate.
 */
const CONTEST_SCOPES = [
  'Aziendale',
  'Per team',
  'Per singolo agente',
  'Per operatore',
  'Per prodotto',
  'Per gruppo di prodotto',
]

/** Minimal shape of `GET /api/operators`'s items — see `OperatorsController.cs`.
 * No dedicated frontend client existed for this endpoint before TASK-0040 (grepped
 * `api/*.ts`); rather than add a new file (outside this task's `allowed_paths`), the
 * fetch is inlined here via the shared `api()` helper, same as several existing pages
 * do for one-off endpoints (e.g. `ContractsPage.tsx`'s `/api/products` call). */
interface OperatorOption { id: number; name: string; isActive: boolean }

interface ContestForm {
  name: string; description: string; contestType: string
  prize: string; startDate: string; endDate: string
  targetOperator: string; targetProductType: string; targetValue: string
  scope: string; sector: string
  // TASK-0250 (REQ-021-001): "Regole personalizzate" — free prose, same string-in-the-form shape as
  // `sector` right above.
  customRules: string
}

const EMPTY_FORM: ContestForm = {
  name: '', description: '', contestType: 'revenue', prize: '', startDate: '', endDate: '',
  targetOperator: '', targetProductType: '', targetValue: '', scope: '', sector: '',
  customRules: '',
}

function statusBadge(s: string) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    draft: { tone: 'neutral', label: 'Bozza' },
    active: { tone: 'success', label: 'Attiva' },
    completed: { tone: 'info', label: 'Completata' },
    cancelled: { tone: 'error', label: 'Annullata' },
  }
  const entry = map[s] ?? { tone: 'neutral' as PillTone, label: s }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function ContestsPage() {
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const [items, setItems] = useState<ContestWithScope[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  // F-101: ordinamento lato server; 'default' = attive per prime, poi fine crescente (quello di sempre).
  const [sort, setSort] = useState('default')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<ContestForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  // Tracks the openEdit backfill fetch (targetOperator/targetProductType aren't on the list
  // projection — see openEdit below). While it's in flight, form.targetOperator/
  // targetProductType are still '' even though the contest may really have them set; saving
  // in that window (or after the fetch fails) would silently overwrite the real values with
  // null via the full-replace PUT (ContestRequest has no partial-update semantics). Gating
  // Save on this — rather than only on `saving` — closes that data-loss window.
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(false)

  const [operators, setOperators] = useState<OperatorOption[]>([])
  // F-142 (REQ-293-001): usi della gara da archiviare, mostrati prima della conferma.
  const [usageDialog, setUsageDialog] = useState<{ contest: ContestWithScope; usages: ConfigurationUsageRow[] } | null>(null)
  const [archiving, setArchiving] = useState(false)

  const pageSize = 25

  // Load the (already-active-only, per OperatorsController.List) operator list once, for
  // the "TargetOperator" select. Not paginated further — matches the small, admin-managed
  // scale every other operator dropdown in this program assumes (e.g. DepartmentsPage.tsx).
  useEffect(() => {
    api<{ items: OperatorOption[] }>('/api/operators?size=200')
      .then(res => setOperators(res.items))
      .catch(() => { /* non-critical — the select just renders empty, field stays optional */ })
  }, [])

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const p: { page: number; status?: string; sort: string } = { page, sort }
      if (statusFilter) p.status = statusFilter
      const res = await listContestsSorted(p)
      setItems(res.items); setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento')
    } finally { setLoading(false) }
  }, [statusFilter, page, sort])

  function handleSort(next: string) { setSort(next); setPage(1) }

  useEffect(() => { void load() }, [load])

  // F-096: dopo "Clona" la copia (bozza) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(items, c => openEdit(c))

  function openCreate() {
    setForm(EMPTY_FORM); setEditId(null); setSaveError('')
    setDetailLoading(false); setDetailError(false)
    setShowModal(true)
  }

  function openEdit(c: ContestWithScope) {
    // `ContestsController.List`'s projection doesn't include targetOperator/targetProductType
    // (only `Get`/`ContestDetail` does — confirmed by reading ContestsController.cs), so `c`
    // (a list row) never has them. Populate the rest immediately from `c`, then backfill the
    // two extra fields with a `contestsApi.get` detail fetch once it resolves.
    setForm({
      name: c.name, description: c.description ?? '', contestType: c.contestType,
      prize: c.prize ?? '', startDate: c.startDate.slice(0, 10), endDate: c.endDate.slice(0, 10),
      // targetValue, scope, sector and customRules, unlike targetOperator/targetProductType, ARE on
      // the list projection (ContestsController.List — TASK-0161/TASK-0183/TASK-0193/TASK-0250), so
      // none of the four needs the detail-fetch backfill below.
      targetOperator: '', targetProductType: '', targetValue: c.targetValue?.toString() ?? '',
      scope: c.scope ?? '', sector: c.sector ?? '', customRules: c.customRules ?? '',
    })
    setEditId(c.id); setSaveError('')
    setDetailLoading(true); setDetailError(false)
    setShowModal(true)
    contestsApi.get(c.id)
      .then((detail: ContestDetailWithScope) => setForm(f => ({
        ...f,
        targetOperator: detail.targetOperator ?? '',
        targetProductType: detail.targetProductType ?? '',
      })))
      .catch(e => {
        setDetailError(true)
        setSaveError(e instanceof Error ? e.message : 'Errore caricamento dettagli gara')
      })
      .finally(() => setDetailLoading(false))
  }

  function closeModal() {
    setShowModal(false); setEditId(null); setSaveError('')
    setDetailLoading(false); setDetailError(false)
  }

  async function handleSave() {
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      setSaveError('Nome, date inizio e fine sono obbligatori')
      return
    }
    setSaving(true); setSaveError('')
    try {
      const payload = {
        name: form.name, contestType: form.contestType,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        ...(form.description ? { description: form.description } : {}),
        ...(form.prize ? { prize: form.prize } : {}),
        // Operator NAME (not id) — ContractContributionReader matches by name,
        // case-insensitive + trimmed server-side (TASK-0038 review fix).
        ...(form.targetOperator ? { targetOperator: form.targetOperator } : {}),
        ...(form.targetProductType.trim() ? { targetProductType: form.targetProductType.trim() } : {}),
        ...(form.targetValue.trim() ? { targetValue: Number(form.targetValue) } : {}),
        // TASK-0183: scope is optional — "" (the "Nessun ambito specificato" option) means "leave
        // it unset", same omit-when-empty convention already used above for prize/targetOperator/
        // targetProductType/targetValue. On Update this deliberately clears a previously set scope
        // to null when left at "" — ContestRequest/PUT is a full replace, no partial-update semantics
        // (same contract already true for targetOperator/targetProductType on this same form).
        ...(form.scope ? { scope: form.scope } : {}),
        // TASK-0193: sector is free text, optional — same omit-when-empty + trim convention as
        // targetProductType right above (not the raw-omit-when-empty of scope/prize, since sector is
        // a plain text input that can carry leading/trailing whitespace).
        ...(form.sector.trim() ? { sector: form.sector.trim() } : {}),
        // TASK-0250: customRules is free prose, optional — same omit-when-empty + trim convention as
        // sector right above. On Update, leaving it blank deliberately clears a previously set value
        // to null (ContestRequest/PUT is a full replace, no partial-update semantics — same contract
        // already true for every other optional field on this form).
        ...(form.customRules.trim() ? { customRules: form.customRules.trim() } : {}),
      }
      if (editId !== null) {
        await contestsApi.update(editId, payload)
      } else {
        await contestsApi.create(payload)
      }
      closeModal(); void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore salvataggio')
    } finally { setSaving(false) }
  }

  async function handleSetStatus(c: ContestWithScope, status: string) {
    try { await contestsApi.updateStatus(c.id, status); void load() }
    catch (e) { snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' }) }
  }

  async function runDelete(c: ContestWithScope, confirm: boolean) {
    setArchiving(true)
    try {
      // F-142 (REQ-293-001): con la guardia accesa e usi bloccanti il server risponde 409 con l'elenco;
      // la finestra chiede conferma e solo allora si ripete con `confirm=true`.
      const res = await deleteContestGuarded(c.id, confirm)
      if (res.status === 'needs-confirm') { setUsageDialog({ contest: c, usages: res.usages }); return }
      setUsageDialog(null)
      if (res.archived) snackbar.show({ message: 'Gara archiviata (annullata): partecipanti e classifica restano consultabili.' })
      void load()
    }
    catch (e) { setUsageDialog(null); snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' }) }
    finally { setArchiving(false) }
  }

  async function handleDelete(c: ContestWithScope) {
    if (!(await confirm({ title: 'Archiviare la gara?', message: `Archiviare la gara "${c.name}"? Se ha partecipanti o non è più in bozza viene annullata e resta in archivio; una bozza vuota viene rimossa.`, confirmLabel: 'Archivia', destructive: true }))) return
    await runDelete(c, false)
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Page>
      <PageHeader
        title="Gare"
        icon="emoji_events"
        count={total > 0 ? total : undefined}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuova gara
          </Button>
        }
      />

      <FilterBar>
        <Select label="Stato" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}>
          <option value="">Tutti gli stati</option>
          <option value="draft">Bozza</option>
          <option value="active">Attiva</option>
          <option value="completed">Completata</option>
          <option value="cancelled">Annullata</option>
        </Select>
        <SortResetLink sort={sort} defaultSort="default" onReset={() => handleSort('default')} />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <SortKeyTh label="Nome" ascKey="name" sort={sort} onSort={handleSort} />
              <th>Tipo</th>
              <th>Premio</th>
              <th>Partecipanti</th>
              <SortKeyTh label="Periodo" ascKey="end_asc" descKey="start_desc" sort={sort} onSort={handleSort} title="Ordina per fine (crescente) o inizio (decrescente)" />
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <DataTableEmptyRow colSpan={7} icon="emoji_events" title="Nessuna gara trovata" />}
            {items.map(c => (
              <tr key={c.id}>
                <td className="md-typescale-emphasized-body-medium">
                  <Link to={`/contests/${c.id}`} className="md-typescale-emphasized-body-medium text-decoration-none">{c.name}</Link>
                </td>
                <td className="text-muted md-typescale-body-small">{CONTEST_TYPE_LABELS[c.contestType] ?? c.contestType}</td>
                <td className="text-muted md-typescale-body-small">
                  {c.prize
                    ? <span><Icon name="emoji_events" size={18} />{' '}{c.prize}</span>
                    : '—'}
                </td>
                <td>
                  <StatusPill tone="primary">
                    {c.participantCount} partecipanti
                  </StatusPill>
                </td>
                <td className="text-muted md-typescale-body-small">
                  {formatDate(c.startDate)} →<br />{formatDate(c.endDate)}
                </td>
                <td>{statusBadge(c.status)}</td>
                <td>
                  <div className="d-flex gap-1">
                    {c.status === 'draft' && (
                      <IconButton icon="play_arrow" size="sm" aria-label="Avvia"
                        onClick={() => void handleSetStatus(c, 'active')} />
                    )}
                    {c.status === 'active' && (
                      <IconButton icon="check_circle" size="sm" aria-label="Completa"
                        onClick={() => void handleSetStatus(c, 'completed')} />
                    )}
                    <CloneConfigurationButton
                      entityLabel="gara"
                      url={`/api/contests/${c.id}/clone`}
                      defaultName={c.name}
                      extraFields={[
                        { key: 'startDate', label: 'Data di inizio', type: 'date', required: true },
                        { key: 'endDate', label: 'Data di fine', type: 'date', required: true },
                      ]}
                      note="Si copia la configurazione della gara. Non si copiano partecipanti e risultati. La copia nasce in bozza."
                      onCloned={async id => {
                        setStatusFilter(''); setPage(1)
                        await load()
                        openAfterClone(id)
                      }}
                    />
                    <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(c)} />
                    <IconButton icon="delete" size="sm" aria-label="Archivia" onClick={() => void handleDelete(c)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
        </div>
      )}

      <Dialog
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica gara' : 'Nuova gara'}
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSave() }}
        actions={
          <>
            {detailError && (
              <span className="text-danger md-typescale-body-small me-auto">
                Impossibile caricare operatore/tipo prodotto correnti — chiudi e riprova per non perderli.
              </span>
            )}
            <Button variant="text" onClick={closeModal}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving || detailLoading || detailError}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
          </>
        }
      >
        {saveError && <Callout tone="error">{saveError}</Callout>}
        <FormSection title="Gara">
          <TextField type="text" label="Nome *" value={form.name}
            className="md-span-full"
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Select label="Tipo gara" value={form.contestType}
            onChange={e => setForm(f => ({ ...f, contestType: e.target.value }))}>
            {Object.entries(CONTEST_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Select label="Ambito" value={form.scope}
            onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
            supportingText={'Opzionale — a chi si applica la gara (aziendale, per team, per singolo agente, per operatore, per prodotto o per gruppo di prodotto). Distinto dal "Tipo gara" qui sopra, che indica quale metrica misura la gara.'}>
            <option value="">Nessun ambito specificato</option>
            {CONTEST_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <TextField multiline rows={2} label="Descrizione" value={form.description}
            className="md-span-full"
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          {/* TASK-0250 (REQ-021-001) — "Regole personalizzate". DEVIATION from the task card,
              disclosed here: the card says to put this input "beside the Conditions input",
              but no Conditions input exists on this form — TASK-0233 added Contest.Conditions
              to the backend only and deliberately never touched this page (verified by
              grepping this file for "conditions": no match before this change). So it is
              placed instead next to Descrizione, the closest analogue on the form: the other
              full-width free-prose textarea. Adding a Conditions input as well would be a
              second requirement's worth of work, outside this task's scope. */}
          <TextField multiline rows={3} label="Regole personalizzate" value={form.customRules}
            className="md-span-full"
            placeholder="es. i contratti annullati entro 30 giorni non vengono conteggiati"
            onChange={e => setForm(f => ({ ...f, customRules: e.target.value }))}
            supportingText="Opzionale, testo libero — eventuali regole specifiche della gara che gli altri campi non esprimono. Sono solo descrittive: non vengono applicate automaticamente al calcolo della classifica." />
        </FormSection>
        <FormSection title="Premio e obiettivo">
          <TextField type="text" label="Premio" placeholder="es. Viaggio, iPhone, Bonus €500"
            value={form.prize} onChange={e => setForm(f => ({ ...f, prize: e.target.value }))} />
          <TextField type="number" min="0" step="0.01" label="Obiettivo"
            placeholder="es. 50000" value={form.targetValue}
            onChange={e => setForm(f => ({ ...f, targetValue: e.target.value }))}
            supportingText="Opzionale — valore numerico dichiarato come obiettivo della gara." />
        </FormSection>
        <FormSection title="Periodo">
          <TextField type="date" label="Data inizio *" value={form.startDate}
            onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
          <TextField type="date" label="Data fine *" value={form.endDate}
            onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
        </FormSection>
        <FormSection title="Perimetro">
          <Select label="Operatore scoperto" value={form.targetOperator}
            onChange={e => setForm(f => ({ ...f, targetOperator: e.target.value }))}
            supportingText={'Opzionale — se impostato, la gara conta solo i contratti di questo operatore. Lasciare "Tutti gli operatori" per una gara scoperta su tutti.'}>
            <option value="">Tutti gli operatori</option>
            {operators.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
          </Select>
          <TextField type="text" label="Tipo prodotto" placeholder="es. pod, pdr, fisso, mobile, web"
            value={form.targetProductType}
            onChange={e => setForm(f => ({ ...f, targetProductType: e.target.value }))}
            supportingText={'Opzionale, testo libero — corrisponde al "Tipo Prodotto" del contratto (stessa terminologia del filtro Pratiche). Lasciare vuoto per una gara scoperta su tutti i prodotti.'} />
          <TextField type="text" label="Settore" placeholder="es. energia, telecomunicazioni, assicurazioni"
            value={form.sector}
            onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}
            supportingText={'Opzionale, testo libero — il settore/ambito merceologico della gara. Distinto dall\'"Ambito" qui sopra, che indica CHI vi partecipa, non di cosa tratta.'} />
        </FormSection>
      </Dialog>
      {/* R3-29: le gare nel ciclo di vita uniforme (solo Admin, flag regole.ciclo_vita). */}
      <RuleLifecyclePanel family="contest" onChanged={() => void load()} />
      {usageDialog && (
        <UsageConfirmDialog
          title={`Archiviare la gara "${usageDialog.contest.name}"?`}
          message="La gara e' in uso: partecipanti, classifica e spese collegate restano consultabili."
          usages={usageDialog.usages}
          confirmLabel="Archivia comunque"
          busy={archiving}
          onConfirm={() => void runDelete(usageDialog.contest, true)}
          onCancel={() => setUsageDialog(null)}
        />
      )}
    </Page>
  )
}
